import type { OperatorType } from "@/types";
import { operatorsLogger } from "@/utils/loggers";

/**
 * Repository for accessing static operator data loaded at build time
 * This serves as a fallback when the PouchDB database is not available
 */
const OperatorJsonRepo = {
  /**
   * Get the last updated timestamp from the static data
   * @returns The timestamp when the static data was generated
   */
  lastUpdatedAt(): number {
    if (!window.operatorsData) {
      operatorsLogger("Static operators data not available");
      return 0;
    }
    return window.operatorsData?.lastUpdated ?? 0;
  },

  /**
   * Get all operators from static data
   * @returns Array of all operators
   */
  getAllOperators(): OperatorType[] {
    if (!window.operatorsData) {
      operatorsLogger("Static operators data not available");
      return [];
    }
    return [...(window.operatorsData?.operators ?? [])];
  },

  /**
   * Find an operator by callsign in static data
   * @param callsign The callsign to search for
   * @returns The operator if found, null otherwise
   */
  findByCallsign(callsign: string): OperatorType | null {
    if (!window.operatorsData) {
      operatorsLogger("Static operators data not available");
      return null;
    }

    const upperCallsign = callsign.toUpperCase();
    const operator = (window.operatorsData?.operators ?? []).find(
      op => op.callsign.toUpperCase() === upperCallsign
    );

    return operator || null;
  },

  /**
   * Search operators by partial callsign match
   * @param query The search query
   * @param limit Maximum number of results
   * @returns Array of matching operators
   */
  search(query: string, limit: number = 50): OperatorType[] {
    if (!window.operatorsData || !window.operatorsData.operators) {
      operatorsLogger("Static operators data not available");
      return [];
    }

    const upperQuery = query.toUpperCase();
    const results: OperatorType[] = [];
    
    // Early-exit loop for better performance with large datasets
    for (const operator of (window.operatorsData?.operators ?? [])) {
      if (operator.callsign.toUpperCase().includes(upperQuery)) {
        results.push(operator);
        if (results.length >= limit) {
          break;
        }
      }
    }

    return results;
  },

  /**
   * Get the total count of operators in static data
   * @returns The number of operators
   */
  getCount(): number {
    if (!window.operatorsData) {
      return 0;
    }
    return window.operatorsData?.totalOperators ?? (window.operatorsData?.operators ?? []).length;
  }
};


export default OperatorJsonRepo;